import { Directive, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * PhoneFormatDirective
 * Automatically formats a phone input to US format: (555) 555-5555
 * Selector: input[appPhoneFormat]
 *
 * - Stores plain digits (e.g. "5555555555") in the form control for backend use
 * - Displays formatted value (e.g. "(555) 555-5555") in the UI input
 */
@Directive({
  selector: 'input[appPhoneFormat]',
  standalone: true,
})
export class PhoneFormatDirective implements OnInit {
  private readonly el = inject(ElementRef<HTMLInputElement>);
  private ngControl = inject(NgControl, { optional: true });

  ngOnInit(): void {
    // When the form control value changes programmatically (e.g. patchValue with plain digits),
    // update the input to show the formatted version.
    this.ngControl?.control?.valueChanges?.subscribe(() => {
      const digits = (this.ngControl!.control!.value ?? '').replace(/\D/g, '').slice(0, 10);
      const formatted = this.format(digits);
      if (this.el.nativeElement.value !== formatted) {
        this.el.nativeElement.value = formatted;
      }
    });
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 10);
    const formatted = this.format(digits);

    // Update the visible input directly to avoid cursor-jump issues
    const cursorPos = this.getNewCursorPos(input.selectionStart ?? 0, formatted);
    input.value = formatted;
    input.setSelectionRange(cursorPos, cursorPos);

    // Store plain digits in the control for backend use
    this.ngControl?.control?.setValue(digits, { emitEvent: false, emitModelToViewChange: false });
  }

  @HostListener('blur')
  onBlur(): void {
    const ctrl = this.ngControl?.control;
    if (!ctrl) return;
    const digits = (ctrl.value ?? '').replace(/\D/g, '').slice(0, 10);
    // Ensure control holds plain digits, input shows formatted
    ctrl.setValue(digits, { emitEvent: false, emitModelToViewChange: false });
  }

  /** Build (555) 555-5555 progressively as digits are typed */
  private format(digits: string): string {
    if (!digits) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  /** Keep cursor after the last digit that was just typed */
  private getNewCursorPos(oldPos: number, formatted: string): number {
    return Math.min(oldPos + 1, formatted.length);
  }
}
